/// <amd-module name="Test/Analytics/TestResultsGrid/Actions/TestResultsGridActionsCreator" />
import { AnnouncementActions } from "Test/Analytics/Common/Actions/AnnouncementActions";
import * as CommonTypes from "Test/Analytics/Common/Types";
import { ConfigurationUtility } from "Test/Analytics/Common/Utils/ConfigurationUtility";
import * as TcmPerfScenarios from "Test/Analytics/Common/Utils/TcmPerfScenarios";
import * as TcmTelemetryConstants from "Test/Analytics/Common/Utils/TcmTelemetryConstants";
import { publishEvent } from "Test/Analytics/Common/Utils/TelemetryService";
import { TestResultsGridActions } from "Test/Analytics/TestResultsGrid/Actions/TestResultsGridActions";
import { DetailedListSourceManager } from "Test/Analytics/TestResultsGrid/DetailedListSourceManager";
import * as Resources from "Test/Analytics/TestResultsGrid/Resources";
import { DetailedTestListDefaultSource } from "Test/Analytics/TestResultsGrid/Source/DetailedTestListDefaultSource";
import { IDetailedTestListSource } from "Test/Analytics/TestResultsGridCommon/Types";
import { FilterValueItem } from "Test/common-util/TestResults.Filtering.Common";
import * as Utils_String from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssPerformanceService } from "VSS/Platform/Performance";

export class TestResultsGridActionsCreator {
    private _detailedTestListDefaultSource: DetailedTestListDefaultSource;
    private _defaultConfValues: CommonTypes.IReportConfiguration = {
        groupBy: CommonTypes.GroupBy.None,
        trendBy: CommonTypes.TrendBy.Days,
        period: CommonTypes.Period.Days_14,
        outcomes: [CommonTypes.TestOutcome.Failed],
        configuredFilters: {
            [CommonTypes.Filter.Workflow.toString()]: {
                values: [new FilterValueItem(CommonTypes.Workflow.Build, CommonTypes.Workflow[CommonTypes.Workflow.Build])]
            }
        }
    };
    private _perfService: IVssPerformanceService;

    constructor(private _actions: TestResultsGridActions, private _announcementActions: AnnouncementActions, private _pageContext: IVssPageContext) {
        this._detailedTestListDefaultSource = new DetailedTestListDefaultSource(_pageContext);
        this._perfService = this._pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
    }

    public updateConfigurationValues(confValues: CommonTypes.IReportConfiguration) {
        this._actions.updateReportsWithConfigurationAction.invoke(confValues);
    }

    public updateDetailedTestList(
        testReportContext: CommonTypes.ITestReportContext,
        confValues: CommonTypes.IReportConfiguration,
        orderByColumn: CommonTypes.IDetailedListColumn,
        nextPageToken: CommonTypes.INextDataPageToken | undefined,
        logTelemetryForFilters?: boolean,
        isUserAction?: boolean
    ): Promise<void> {
        this._perfService.startScenario(TcmPerfScenarios.TestAX_ResultsGrid, !!isUserAction);

        if (ConfigurationUtility.areConfValuesEqual(this._defaultConfValues, confValues) && !isUserAction) {
            this._actions.initializingTestResultsAction.invoke(null);
            this._actions.updateReportsWithConfigurationAction.invoke(confValues);
            return this._detailedTestListDefaultSource.queryDefaultDetailedList(testReportContext, null).then(
                (detailedListData: CommonTypes.IDetailedListData) => {
                    this._actions.updateDetailedTestListAction.invoke(detailedListData);
                    this._announcementActions.announceAction.invoke(
                        Utils_String.localeFormat(Resources.DetailedTestListLoaded, detailedListData.detailedListItems.length)
                    );
                    this.logTelemetryAndPerfForDetailedList(
                        testReportContext,
                        confValues,
                        orderByColumn,
                        detailedListData.detailedListItems.length,
                        detailedListData.isCachedData,
                        isUserAction,
                        logTelemetryForFilters
                    );
                },
                (error: Error) => {
                    this._actions.onError.invoke(error);
                    this._perfService.endScenario(TcmPerfScenarios.Area, TcmPerfScenarios.TestAX_ResultsGrid, !!isUserAction, undefined, {
                        status: "error"
                    });
                }
            );
        } else {
            const detailedListSourceManager = new DetailedListSourceManager(this._pageContext);
            return detailedListSourceManager
                .getDetailedListSourceManager()
                .then((detailedListSource: IDetailedTestListSource) => {
                    if (confValues.groupBy == CommonTypes.GroupBy.None) {
                        detailedListSource.queryDetailedList(testReportContext, confValues, orderByColumn, null, "").then(
                            (detailedListData: CommonTypes.IDetailedListData) => {
                                this._actions.updateDetailedTestListAction.invoke(detailedListData);
                                this._announcementActions.announceAction.invoke(
                                    Utils_String.localeFormat(Resources.DetailedTestListLoaded, detailedListData.detailedListItems.length)
                                );
                                this.logTelemetryAndPerfForDetailedList(
                                    testReportContext,
                                    confValues,
                                    orderByColumn,
                                    detailedListData.detailedListItems.length,
                                    detailedListData.isCachedData,
                                    isUserAction,
                                    logTelemetryForFilters
                                );
                            },
                            (error: Error) => {
                                this._actions.onError.invoke(error);
                                this._perfService.endScenario(TcmPerfScenarios.Area, TcmPerfScenarios.TestAX_ResultsGrid, !!isUserAction, undefined, {
                                    status: "error"
                                });
                            }
                        );
                    } else {
                        detailedListSource.queryGroupedDetailedList(testReportContext, confValues, orderByColumn, null).then(
                            (detailedListData: CommonTypes.IDetailedListData) => {
                                this._actions.updateDetailedTestListAction.invoke(detailedListData);
                                this._announcementActions.announceAction.invoke(
                                    Utils_String.localeFormat(
                                        Resources.DetailedTestListLoadedWithGroupBy,
                                        CommonTypes.GroupBy[confValues.groupBy],
                                        detailedListData.detailedListItems.length
                                    )
                                );
                                this.logTelemetryAndPerfForDetailedList(
                                    testReportContext,
                                    confValues,
                                    orderByColumn,
                                    detailedListData.detailedListItems.length,
                                    detailedListData.isCachedData,
                                    isUserAction,
                                    logTelemetryForFilters
                                );
                            },
                            (error: Error) => {
                                this._actions.onError.invoke(error);
                                this._perfService.endScenario(TcmPerfScenarios.Area, TcmPerfScenarios.TestAX_ResultsGrid, !!isUserAction, undefined, {
                                    status: "error"
                                });
                            }
                        );
                    }
                })
                .catch((reason: any) => {
                    this._actions.onError.invoke(reason);
                });
        }
    }

    // This is show more method for both plain test list and grouped list.
    public async detailedListShowMore(
        testReportContext: CommonTypes.ITestReportContext,
        confValues: CommonTypes.IReportConfiguration,
        orderByColumn: CommonTypes.IDetailedListColumn,
        nextPageToken: CommonTypes.INextDataPageToken | undefined
    ): Promise<void> {
        this._perfService.startScenario(TcmPerfScenarios.TestAX_ResultsGridLoadMore, false);

        const detailedListSourceManager = new DetailedListSourceManager(this._pageContext);
        const detailedListSource: IDetailedTestListSource = await detailedListSourceManager.getDetailedListSourceManager();
        return detailedListSource.queryGroupedDetailedList(testReportContext, confValues, orderByColumn, nextPageToken).then(
            (detailedTestListData: CommonTypes.IDetailedListData) => {
                this._actions.detailedListShowMoreAction.invoke(detailedTestListData);
                this._announcementActions.announceAction.invoke(
                    Utils_String.localeFormat(Resources.DetailedListShowMore, detailedTestListData.detailedListItems.length)
                );

                this._perfService.endScenario(TcmPerfScenarios.Area, TcmPerfScenarios.TestAX_ResultsGridLoadMore, false);

                const telemetryData = this.createTelemetryForDetailedList(
                    confValues,
                    orderByColumn,
                    detailedTestListData.detailedListItems.length,
                    detailedTestListData.isCachedData
                );
                telemetryData[TcmTelemetryConstants.Type] =
                    confValues.groupBy === CommonTypes.GroupBy.None ? TcmTelemetryConstants.Test : TcmTelemetryConstants.Group;

                publishEvent(
                    this._pageContext,
                    TcmTelemetryConstants.featureTestAX_ResultsGridLoadMore,
                    telemetryData,
                    testReportContext,
                    confValues
                );
            },
            (error: Error) => {
                this._actions.onError.invoke(error);
            }
        );
    }

    // This is show more method inside a group.
    public async detailedListShowMoreInsideGroup(
        testReportContext: CommonTypes.ITestReportContext,
        confValues: CommonTypes.IReportConfiguration,
        orderByColumn: CommonTypes.IDetailedListColumn,
        nextPageToken: CommonTypes.INextDataPageToken | undefined,
        parentItemKey: number | string
    ): Promise<void> {
        this._perfService.startScenario(TcmPerfScenarios.TestAX_ResultsGridLoadMore, false);

        const detailedListSourceManager = new DetailedListSourceManager(this._pageContext);
        const detailedListSource: IDetailedTestListSource = await detailedListSourceManager.getDetailedListSourceManager();
        return detailedListSource
            .queryGroupedDetailedList(testReportContext, confValues, orderByColumn, nextPageToken)
            .then((detailedListData: CommonTypes.IDetailedListData) => {
                const detailedListGroupData: CommonTypes.IDetailedListGroupData = {
                    detailedListGroupItem: {
                        groupItem: { itemkey: parentItemKey } as CommonTypes.IDetailedListItem,
                        children: detailedListData.detailedListItems,
                        nextPageToken: detailedListData.nextPageToken
                    } as CommonTypes.IDetailedListGroupItem,
                    confValues: detailedListData.confValues,
                    isCachedData: detailedListData.isCachedData
                };
                this._actions.detailedListShowMoreInsideGroupAction.invoke(detailedListGroupData);
                this._announcementActions.announceAction.invoke(
                    Utils_String.localeFormat(Resources.DetailedListShowMoreInsideGroup, detailedListData.detailedListItems.length, parentItemKey)
                );

                this._perfService.endScenario(TcmPerfScenarios.Area, TcmPerfScenarios.TestAX_ResultsGridLoadMore, false);

                const telemetryData = this.createTelemetryForDetailedList(
                    confValues,
                    orderByColumn,
                    detailedListData.detailedListItems.length,
                    detailedListData.isCachedData
                );
                telemetryData[TcmTelemetryConstants.Type] = TcmTelemetryConstants.Test;

                publishEvent(
                    this._pageContext,
                    TcmTelemetryConstants.featureTestAX_ResultsGridLoadMore,
                    telemetryData,
                    testReportContext,
                    confValues
                );
            });
    }

    public detailedTestListColumnOrderChanged(orderedColumn: CommonTypes.IDetailedListColumn) {
        this._actions.detailedTestListColumnOrderChangedAction.invoke(orderedColumn);
    }

    public detailedTestListSortByColumn() {
        this._actions.detailedTestListSortByColumnAction.invoke(null);
    }

    private logTelemetryAndPerfForDetailedList(
        testReportContext: CommonTypes.ITestReportContext,
        confValues: CommonTypes.IReportConfiguration,
        orderByColumn: CommonTypes.IDetailedListColumn,
        testsListLength: number,
        isCached?: boolean,
        isUserAction?: boolean,
        logTelemetryForFilters?: boolean
    ) {
        this._perfService.endScenario(TcmPerfScenarios.Area, TcmPerfScenarios.TestAX_ResultsGrid, !!isUserAction);

        const telemetryData = this.createTelemetryForDetailedList(confValues, orderByColumn, testsListLength, isCached, isUserAction);

        publishEvent(this._pageContext, TcmTelemetryConstants.featureTestAX_ResultsGrid, telemetryData, testReportContext, confValues);

        if (logTelemetryForFilters) {
            this._publishTelemetryForFilters(testReportContext, confValues, testsListLength);
        }
    }

    private createTelemetryForDetailedList(
        confValues: CommonTypes.IReportConfiguration,
        orderByColumn: CommonTypes.IDetailedListColumn,
        testsListLength: number,
        isCached?: boolean,
        isUserAction?: boolean
    ): { [key: string]: string | number } {
        const filtersApplied: string[] = confValues.configuredFilters ? Object.keys(confValues.configuredFilters) : [];
        const telemetryData: { [key: string]: string | number } = {
            [TcmTelemetryConstants.SortBy]: CommonTypes.ColumnIndices[orderByColumn.column].toString(),
            [TcmTelemetryConstants.GroupBy]: CommonTypes.GroupBy[confValues.groupBy].toString(),
            [TcmTelemetryConstants.Duration]: CommonTypes.Period[confValues.period].toString(),
            [TcmTelemetryConstants.Rows]: testsListLength,
            [TcmTelemetryConstants.Filters]: filtersApplied.length ? filtersApplied.toString() : TcmTelemetryConstants.Clear,
            [TcmTelemetryConstants.FiltersCount]: filtersApplied.length,
            [TcmTelemetryConstants.IsUserAction]: String(isUserAction),
            [TcmTelemetryConstants.IsCached]: String(isCached)
        };
        return telemetryData;
    }

    private _publishTelemetryForFilters(
        testReportContext: CommonTypes.ITestReportContext,
        confValues: CommonTypes.IReportConfiguration,
        rowsCount: number
    ) {
        const filtersApplied: string[] = Object.keys(confValues.configuredFilters);
        if (filtersApplied.length) {
            const telemetryData: { [key: string]: string | number } = {
                [TcmTelemetryConstants.Filters]: filtersApplied.length ? filtersApplied.toString() : TcmTelemetryConstants.Clear,
                [TcmTelemetryConstants.FiltersCount]: filtersApplied.length,
                [TcmTelemetryConstants.Rows]: rowsCount
            };

            publishEvent(this._pageContext, TcmTelemetryConstants.featureTestAX_Filter, telemetryData, testReportContext, confValues);
        }
    }
}
